import { ChangeDetectionStrategy, Component, Inject, type OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from "@angular/router";
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import type { MenuItem } from "primeng/api";
import * as FlatModels from "@medsurf/flat-models";
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from "@medsurf/flat-facades";
import * as FlatServices from '@medsurf/flat-services';
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { ModalControlComponent } from "@medsurf/flat-components";
import { FLAT_SETTINGS, type SettingControlModel } from "@medsurf/flat-services";
import { PageLinkDialogComponent } from "../../common/page-link-dialog/page-link-dialog.component";

@Component({
    selector: 'medsurf-detached-pages',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detached-pages.component.html',
    styleUrls: ['./detached-pages.component.scss'],
    standalone: false
})
export class DetachedPagesComponent implements OnInit {
  @ViewChild('searchStringInputText', {static: false}) searchStringInputText: FlatDesigns.TextInputFormControlDesign;
	
	#isSubmitted = signal(false);
	#handled = false;
	#typedNodeExpanded: { [id: string]: boolean } = {};
  searchForm = new FormGroup(
    {
      searchString: new FormControl(''),
      createdByMe: new FormControl(false),
      sortField: new FormControl(''),
      sortOrder: new FormControl(0),
      rows: new FormControl(25),
      first: new FormControl(0)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.SearchControlModels.NodeSearchSchema.pick({
          searchString: true,
          createdByMe: true,
          sortField: true,
          sortOrder: true,
          rows: true,
          first: true,
        })
      ),
      updateOn: 'change',
    },
  );
	getSlidesMenuItems = () => {
		const selectPageSet = this.bulkEditAuthorFacade.snapshot_selectPageSet();
		const disabled = selectPageSet.size === 0;
		return [
			{
				label: this.translate.instant('global.deselect'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SELECTION_REMOVE,
				command: () => this.deselectPages(),
			},
			{
				label: this.translate.instant('global.pages_linking'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH,
				command: () => this.bulkAttachPages(),
			},
			{
				separator: true,
			},
			{
				label: this.translate.instant('global.remove'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
				styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
				command: () => this.bulkRemovePages(),
			}
		] satisfies MenuItem[];
	}
  readonly FlatModels = FlatModels;
  readonly Array = Array;
  
  constructor(private alertControlFacade: FlatFacades.AlertControlFacade,
              private bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public searchControlFacade: FlatFacades.SearchControlFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public mediaControlService: FlatServices.MediaControlService,
              private dialogService: DialogService,
              private translate: TranslateService,
              private router: Router,
              @Inject(FLAT_SETTINGS) public settings: SettingControlModel) {
    this.slideAuthorFacade.typedNodeExpanded$.pipe(takeUntilDestroyed()).subscribe((typedNodeExpanded) => {
      this.#typedNodeExpanded = typedNodeExpanded;
    });
		
    this.historySharedFacade.ofActionDispatchedSaveSnapshotsSuccess.pipe(takeUntilDestroyed()).subscribe(() => {
      this.onSubmit();
    });
  }
  
  ngOnInit() {
    this.bulkEditAuthorFacade.requestResetPages().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    
    // Fire Search
    this.onSubmit();
    
    // Autofocus
    setTimeout(() => {
      this.searchStringInputText?.inputElement?.nativeElement?.focus();
    });
  }
  
  onSubmit(resetPage = true){
    if (this.#isSubmitted() || this.searchForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    if (resetPage) this.searchForm.controls.first.setValue(0);
    
    const search: FlatModels.SearchControlModels.NodeSearch = {
      searchString: this.searchForm.value.searchString || '',
      createdByMe: this.searchForm.value.createdByMe || false,
      sortField: this.searchForm.value.sortField || '',
      sortOrder: this.searchForm.value.sortOrder || 0,
      rows: this.searchForm.value.rows || 25,
      first: this.searchForm.value.first || 0,
      exact: false
    };
    this.searchControlFacade.requestFindDetachedPages(search).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.searchForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onLazy(event: any){
    this.searchForm.controls?.['first']?.setValue(event?.first);
  }
  
  onPageClick(event: MouseEvent,
                     id: string) {
    if (this.#handled) {
      this.#handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestTogglePage(id).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      const url: string = this.router.serializeUrl(this.router.createUrlTree(['slide', id]));
      window.open(url, '_blank');
    }
  }
  
  changeLayout(event: any) {
    this.slideAuthorFacade.requestSetLayout(event.layout).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  onToggleBulkPage(id: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestTogglePage(id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  onToggleBulkPages(pageSet: Set<string>,
                    override: boolean) {
    if (override) {
      this.bulkEditAuthorFacade.requestSetPages(Array.from(pageSet)).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      this.bulkEditAuthorFacade.requestRemovePages(Array.from(pageSet)).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  deselectPages() {
	  this.bulkEditAuthorFacade.requestDeselectPages().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  bulkAttachPages() {
	  const dialogRef = this.dialogService.open(PageLinkDialogComponent, {
		  header: this.translate.instant('global.pages_link_title'),
		  dismissableMask: true,
		  width: '70%',
		  height: '90%',
	  });
	  dialogRef.onClose.subscribe(data => {
		  if (data?.result) {
			  this.bulkEditAuthorFacade.requestAttachSelectedPagesToNodes(data.nodes).subscribe({
				  error: (result) => this.alertControlFacade.requestAddError(result),
				  next: () => {
					  for(const node of data.nodes) {
						  this.#typedNodeExpanded[node.id] = true;
						  this.slideAuthorFacade.requestSetTypedNodeExpanded(this.#typedNodeExpanded).subscribe({
							  error: (result) => this.alertControlFacade.requestAddError(result),
						  });
					  }
					  this.alertControlFacade.requestAddAlert({
						  key: 'main',
						  detail: 'notification.slides_linked',
						  severity: FlatModels.AlertControlModels.AlertType.enum.success,
						  life: FlatModels.AlertControlModels.AlertTime.DEFAULT
					  });
				  }
			  });
		  }
	  });
  }
  
  bulkRemovePages() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.pages_remove'),
      data: {
        label: 'global.pages_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        this.bulkEditAuthorFacade.requestRemoveBulkEditPages().subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            this.onSubmit();
          },
        });
      }
    });
  }
  
  trackBy(index: number,
                 item: FlatModels.PageEntityModels.PageEntityType) {
    return item.id || index;
  }
  
  executeUndo() {
    if (this.historySharedFacade.snapshot_canUndo()) {
      this.historySharedFacade.requestUndoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeRedo() {
    if (this.historySharedFacade.snapshot_canRedo()) {
      this.historySharedFacade.requestRedoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeSave() {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }
  
  openNodePreview(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
          // Navigate To Url
          const url = `${this.settings.viewerUrl}${routeElement.path}`;
          open(url, '_medsurf_preview');
        }
      });
    } else {
      // Navigate To Url
      const url = `${this.settings.viewerUrl}${routeElement.path}`;
      open(url, '_medsurf_preview');
    }
  }
}
